import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidEmail, sendPublicEmail } from "../Services/publicEmailService";

export default function PublicContactForm() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [pending, setPending] = useState(false);
  const sending = useRef(false);

  const submit = async (event) => {
    event.preventDefault();
    if (sending.current) return;
    const form = event.currentTarget;
    const fields = Object.fromEntries([...new FormData(form)].map(([key, value]) => [key, value.trim()]));
    const nextErrors = {};
    ["lastName", "firstName", "email", "message"].forEach((key) => {
      if (!fields[key]) nextErrors[key] = "required";
    });
    if (fields.email && (!isValidEmail(fields.email) || !form.elements.email.validity.valid)) nextErrors.email = "invalidEmail";
    setErrors(nextErrors);
    setStatus("");
    if (Object.keys(nextErrors).length) { form.elements[Object.keys(nextErrors)[0]].focus(); return; }
    sending.current = true;
    setPending(true);
    try {
      await sendPublicEmail("contact", fields);
      form.reset();
      setStatus("contactSuccess");
    } catch (error) {
      setStatus("sendError");
    } finally {
      sending.current = false;
      setPending(false);
    }
  };

  return <form onSubmit={submit} noValidate className="border-t border-slate-200 pt-6" aria-busy={pending}>
    <h2 className="mb-5 text-xl font-semibold text-slate-900">{t("publicEmail.formTitle")}</h2>
    <fieldset disabled={pending} className="grid min-w-0 gap-4 sm:grid-cols-2">
      {["lastName", "firstName", "email", "phone", "message"].map((name) => {
        const props = {
          id: `contact-${name}`, name, required: name !== "phone",
          maxLength: name === "message" ? 5000 : name === "email" ? 254 : name === "phone" ? 40 : 100,
          "aria-invalid": Boolean(errors[name]), "aria-describedby": errors[name] ? `contact-${name}-error` : undefined,
          className: "block w-full min-w-0 rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20",
          onChange: () => { setErrors((previous) => ({ ...previous, [name]: undefined })); setStatus(""); },
        };
        return <div key={name} className={name === "message" ? "sm:col-span-2" : ""}>
          <label htmlFor={props.id} className="mb-2 block text-sm font-semibold text-slate-900">{t(`publicEmail.${name}`)} {name !== "phone" && <span aria-hidden="true">*</span>}</label>
          {name === "message" ? <textarea {...props} rows={5} /> : <input {...props} type={name === "email" ? "email" : name === "phone" ? "tel" : "text"} dir={name === "phone" || name === "email" ? "ltr" : undefined} autoComplete={{ lastName: "family-name", firstName: "given-name", email: "email", phone: "tel" }[name]} />}
          {errors[name] && <p id={`contact-${name}-error`} role="alert" className="mt-1 text-sm text-red-700">{t(`publicEmail.${errors[name]}`)}</p>}
        </div>;
      })}
      <button type="submit" className="rounded-xl bg-green-600 px-6 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60 sm:justify-self-start">{t(`publicEmail.${pending ? "sending" : "send"}`)}</button>
    </fieldset>
    <p role="status" aria-live="polite" className={`mt-3 text-sm ${status === "contactSuccess" ? "text-green-700" : "text-red-700"}`}>{status && t(`publicEmail.${status}`)}</p>
  </form>;
}
